// noinspection GraphQLUnresolvedReference

import { gql } from 'graphql-request';

export const MetaFragment = gql`
  fragment Meta on Meta {
    name
    schema
    deliveryKey
    deliveryId
    url
  }
`;

export const LinkFragment = gql`
  fragment Link on Link {
    link
    externalLink
    isExternal
    displayName
    asButton
    anchorTag
  }
`;

export const AssetFragment = gql`
  fragment Asset on Assets {
    accessibilityLabel
    name
    url
    defaultHost
    endpoint
  }
`;

export const VideoFragment = gql`
  fragment Video on Video {
    contentUrl
    duration
    embedUrl
    externalVideoId
    name
    source
    thumbnailUrl
    uploadDate
    uploadedThumbnail {
      ...Asset
    }
    videoAsset {
      defaultHost
      endpoint
      id
      name
    }
  }
`;

export const ShopableListFragment = gql`
  fragment ShopableList on ShopableList {
    _meta {
      ...Meta
    }
    icon {
      ...Asset
    }
    title
    skus
    description
  }
`;

export const SeoFragment = gql`
  fragment Seo on SEO {
    noIndex
    noFollow
    ogTitle
    ogDescription
    ogType
    twitterCard
    twitterCreator
    pageType
    ogImage {
      ...Asset
    }
    _meta {
      ...Meta
    }
  }
`;

export const BreadCrumbFragment = gql`
  fragment BreadCrumb on BreadCrumb {
    _meta {
      ...Meta
    }
    breadcrumb {
      displayName
      isExternal
      noFollow
      noIndex
      link
      externalLink
    }
    dark
    showSocial
  }
`;

export const ImageFragment = gql`
  fragment Image on Image {
    _meta {
      schema
    }
    altTag
    image {
      ...Asset
    }
    static
  }
`;

export const LinkWithIconFragment = gql`
  fragment LinkWithIcon on LinkWithIcon {
    _meta {
      ...Meta
    }
    displayName
    externalLink
    isExternal
    link
    noFollow
    noIndex
    anchorTag
    imageContent {
      ...Image
    }
  }
  ${ImageFragment}
`;

export const LinkGroupFragment = gql`
  fragment LinkGroup on LinkGroup {
    _meta {
      ...Meta
    }
    title
    links {
      ...LinkWithIcon
    }
  }
`;

export const LinkListFragment = gql`
  fragment LinkList on LinkList {
    title
    columns {
      ...LinkGroup
    }
    inline
    _meta {
      ...Meta
    }
  }
`;

export const NavProductCardFragment = gql`
  fragment NavProductCard on NavProductCard {
    _meta {
      ...Meta
    }
    image {
      ...Image
    }
    hoverImage {
      ...Image
    }
    links {
      ...Link
    }
    mainLink {
      ...Link
    }
    price
    title
    badge
  }
`;

export const ProductListFragment = gql`
  fragment ProductList on ProductList {
    _meta {
      ...Meta
    }
    products {
      ...NavProductCard
    }
    title
  }
  ${NavProductCardFragment}
`;
export const NavDropdownFragment = gql`
  fragment NavDropdown on NavDropdown {
    _meta {
      ...Meta
    }
    description
    navigationPromo {
      align
      links {
        ...Link
      }
      title
      image {
        ...Image
      }
    }
    centerContent {
      ...LinkGroup
      ...LinkList
      ...ProductList
    }
    rightContent {
      links {
        ...Link
      }
      socialLinks {
        title
        links {
          ...LinkWithIcon
        }
      }
      search {
        searchCategory: searchBy
        placeholder: searchPlaceHolder
      }
    }
    title
  }
`;

export const SitewideBannerFragment = gql`
  fragment SitewideBanner on SitewideBanner {
    sitewidePromotions {
      countdownClock {
        startDate
        startTime
        endDate
        endTime
        endsInText
      }
      link {
        ...Link
      }
      text
      promotionId
      isDefault
      rotationTime
    }
    dealerLocatorLink {
      ...Link
    }
  }
`;

export const SecondaryNavFragment = gql`
  fragment SecondaryNav on SecondaryNav {
    _meta {
      schema
    }
    items {
      ...Link
      ...NavDropdown
    }
  }
`;

export const PageHeaderFragment = gql`
  fragment PageHeader on PageHeader {
    json
    searchLink {
      ...Link
    }
    sitewideBanner {
      ...SitewideBanner
    }
    account {
      greeting
      link {
        ...Link
      }
      loggedInLabel
      loggedOutLabel
    }
    bottomMobileLinks {
      ...Link
    }
    navItems {
      title
      titleAsLink {
        ...Link
      }
      _meta {
        ...Meta
      }
      content {
        ...NavDropdown
        ...SecondaryNav
      }
    }
    searchLink {
      ...Link
    }
  }
  ${SecondaryNavFragment}
  ${NavDropdownFragment}
  ${LinkGroupFragment}
  ${LinkListFragment}
  ${LinkWithIconFragment}
  ${SitewideBannerFragment}
  ${ProductListFragment}
`;

export const RecipeFragment = gql`
  fragment Recipe on Recipe {
    id
    _meta {
      ...Meta
    }
    contentRows {
      json
    }
    recipeMetaSection {
      isFeatured
      title
      keywords
      relatedCategories
      description
      shortDescription
      cookTime
      prepTime
      complexity
      hardwood
      servingSizeMin
      servingSizeMax
    }
    beforeYouGetStarted {
      title
      summary
    }
    steps {
      cookCycleInfo {
        _meta {
          ...Meta
        }
        cookingLength
        cookingMethod
        grillTemp
        grillTemperature {
          celsius
          fahrenheit
        }
        inductionHob {
          hobPowerLevels
          hobTimer
        }
        keepWarm
        probeTarget
        probeTargetTemperature {
          celsius
          fahrenheit
        }
        superSmoke
      }
      ingredientGroup
      title
      instruction
      stepIndex
      Ingredients {
        unitOfMeasure
        imperial {
          measurement
          measurementDisplay
          unitOfMeasure
        }
        metric {
          measurement
          measurementDisplay
          unitOfMeasure
        }
        ingredient {
          name
          product {
            ...Product
          }
        }
      }
      proTip {
        tip
        link {
          ...Link
        }
      }
      assets {
        ...Asset
      }
      youtubeLink {
        name
        contentUrl
        thumbnailUrl
      }
    }
    grillCommand
    list {
      ...ShopableList
    }
    author {
      id
      name
    }
    assets {
      ...Asset
    }
    SEO {
      ...Seo
    }
    notes {
      id
      recipeId
      recipeIdAndPrivacy
      recipeIdAndUserId
      recipeIdAndUserIdAndStepIndex
      createdAt
      createdByUserId
      updatedAt
      upVotes
      downVotes
      stepIndex
      parentRecipeIdAndUserId
      comment
      privacy
    }
    ratingsAndReviews {
      totalReviews
      ratingSummary {
        average
        count
      }
    }
    youtubeLink {
      name
      contentUrl
      duration
      embedUrl
      thumbnailUrl
      uploadDate
      hostKey
    }
    savedByUser
    ingredientSummary {
      hasMetric
      hasImperial
      groups {
        groupKey
        groupName
        ingredients {
          imperial {
            ...Measure
          }
          metric {
            ...Measure
          }
          unitOfMeasure
          ingredient {
            name
            product {
              ...Product
            }
          }
        }
      }
    }
    saveCount
    servingSizes {
      people
    }
    recipeMigrationSection {
      slugId
    }
    shoppableTools {
      title
      products {
        ...Product
      }
    }
  }
`;

export const RecipeCategoryBodyFragment = gql`
  fragment RecipeCategoryBody on RecipeCategoryBody {
    _meta {
      ...Meta
    }
    category
    categoryFilters {
      _meta {
        ...Meta
      }
      filterType
      options {
        filter
        title
      }
      title
    }
    contentRows {
      json
    }
    recipeFilters {
      name
      options
    }
    json
  }
`;

export const LearnFragment = gql`
  fragment Learn on LearningContent {
    _meta {
      ...Meta
    }
    articleCollection {
      navLinks {
        ...Link
      }
      title
    }
    articleModules {
      headerText
      image {
        ...Asset
      }
      markdownText
      pullQuote
      youtubeVideo {
        ...Video
      }
    }
    articleType
    components
    featuredContent
    headerAsset {
      image {
        ...Asset
      }
      youtubeLink {
        ...Video
      }
    }
    id
    locale
    navigation
    publicationDate
    SEO: seo {
      ...Seo
    }
    tags {
      tags
    }
    title
    type {
      categories
    }
    recipe {
      _meta {
        ...Meta
      }
      ingredientSummary {
        groups {
          ingredients {
            ingredient {
              name
            }
          }
        }
      }
      id
      author {
        name
        avatar {
          url
        }
      }
      assets {
        url
      }
      recipeMetaSection {
        title
        description
        cookTime
        prepTime
        hardwood
        servingSizeMin
      }
      ratingSummary {
        average
        count
      }
      steps {
        cookCycleInfo {
          _meta {
            ...Meta
          }
          cookingLength
          cookingMethod
          grillTemp
          grillTemperature {
            celsius
            fahrenheit
          }
          inductionHob {
            hobPowerLevels
            hobTimer
          }
          keepWarm
          probeTarget
          probeTargetTemperature {
            celsius
            fahrenheit
          }
          superSmoke
        }
        ingredientGroup
        title
        instruction
        stepIndex
      }
      ingredientSummary {
        hasMetric
        hasImperial
        groups {
          groupKey
          groupName
          ingredients {
            imperial {
              ...Measure
            }
            metric {
              ...Measure
            }
            unitOfMeasure
            ingredient {
              name
            }
          }
        }
      }
    }
  }
`;

export const MeasureFragment = gql`
  fragment Measure on Measure {
    measurement
    measurementDisplay
    unitOfMeasure
    weight
  }
`;

export const IngredientFragment = gql`
  ${MeasureFragment}

  fragment Ingredient on RecipeIngredients {
    imperial {
      ...Measure
    }
    metric {
      ...Measure
    }
    unitOfMeasure
    ingredient {
      name
    }
  }
`;
